/** Browser Notification API helpers for estimate follow-up alerts. */

export type BrowserPermission = NotificationPermission | 'unsupported';

const DISMISS_KEY = 'mrt_push_permission_dismissed';

export function isBrowserNotificationSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
}

export function getBrowserNotificationPermission(): BrowserPermission {
    if (!isBrowserNotificationSupported()) return 'unsupported';
    return Notification.permission;
}

/** True when the user has not yet answered the browser prompt. */
export function needsBrowserNotificationPermission(): boolean {
    return getBrowserNotificationPermission() === 'default';
}

export function wasPushPermissionPromptDismissed(): boolean {
    try {
        return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
        return false;
    }
}

export function dismissPushPermissionPrompt(): void {
    try {
        sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
        // Ignore private-mode / blocked storage.
    }
}

export function clearPushPermissionPromptDismiss(): void {
    try {
        sessionStorage.removeItem(DISMISS_KEY);
    } catch {
        // Ignore.
    }
}

/**
 * Shows the browser permission dialog. Must run from a user gesture
 * (button click) — browsers block silent prompts.
 */
export async function requestBrowserNotificationPermission(): Promise<BrowserPermission> {
    if (!isBrowserNotificationSupported()) return 'unsupported';
    if (Notification.permission !== 'default') {
        return Notification.permission;
    }
    try {
        return await Notification.requestPermission();
    } catch {
        return Notification.permission;
    }
}

type ShowOptions = {
    title?: string;
    body: string;
    tag?: string;
    onClick?: () => void;
};

/**
 * Desktop/OS notification when permission is granted.
 * No-op if unsupported, denied, or the page is focused (Snackbar covers that).
 */
export function showBrowserNotification(options: ShowOptions): Notification | null {
    if (getBrowserNotificationPermission() !== 'granted') return null;
    // Prefer in-app toast when the dashboard tab is already visible.
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        return null;
    }
    try {
        const n = new Notification(options.title ?? 'MRT Dashboard', {
            body: options.body,
            tag: options.tag,
            icon: '/mrt-logo.svg',
            requireInteraction: false,
        });
        if (options.onClick) {
            n.onclick = () => {
                window.focus();
                options.onClick?.();
                n.close();
            };
        }
        return n;
    } catch {
        return null;
    }
}
